/**
 * IndexedDB-backed persistence for the consumer wizard's in-progress state.
 *
 * Used by the guest-checkout flow at /requests/new to survive auth-flow redirects
 * (most importantly Google SSO, which is a full-page redirect). Stores form values
 * AND File/Blob objects — IndexedDB serializes Blobs natively, unlike localStorage.
 *
 * Failure modes are deliberately silent: if IndexedDB is unavailable (iOS Safari
 * private browsing, locked-down corporate browsers, quota exhausted) we no-op. The
 * worst-case fallback is the original behaviour (state lost on redirect) — acceptable
 * for the early-funnel design.
 */

const DB_NAME = "gestionalo_wizard";
const DB_VERSION = 1;
const STORE_NAME = "snapshots";

/** Snapshots older than this are treated as gone. Keeps stale carts out of the wizard. */
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

const SESSION_UUID_LS_KEY = "gestionalo_wizard_session_uuid";

interface SnapshotEntry<T> {
  uuid: string;
  state: T;
  timestamp: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function isIndexedDbAvailable(): boolean {
  try {
    return typeof indexedDB !== "undefined" && indexedDB !== null;
  } catch {
    return false;
  }
}

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (!isIndexedDbAvailable()) {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "uuid" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
    req.onblocked = () => reject(new Error("IndexedDB upgrade blocked"));
  });
  // Reset the cached promise on failure so the next call gets a fresh attempt.
  dbPromise.catch(() => {
    dbPromise = null;
  });
  return dbPromise;
}

export async function snapshotWizardState<T>(uuid: string, state: T): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const entry: SnapshotEntry<T> = { uuid, state, timestamp: Date.now() };
      tx.objectStore(STORE_NAME).put(entry);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("snapshot write failed"));
      tx.onabort = () => reject(tx.error ?? new Error("snapshot transaction aborted"));
    });
  } catch {
    // Silent degrade — the wizard keeps working from React state.
  }
}

export interface HydrateOptions {
  /** Override the default 24h staleness window. */
  ttlMs?: number;
}

export async function hydrateWizardState<T>(
  uuid: string,
  options: HydrateOptions = {},
): Promise<T | null> {
  try {
    const db = await openDb();
    const entry = await new Promise<SnapshotEntry<T> | null>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).get(uuid);
      req.onsuccess = () => resolve((req.result as SnapshotEntry<T> | undefined) ?? null);
      req.onerror = () => reject(req.error ?? new Error("hydrate read failed"));
    });
    if (!entry) return null;
    const ttl = options.ttlMs ?? DEFAULT_TTL_MS;
    if (Date.now() - entry.timestamp > ttl) {
      // Stale — drop it so future hydrate calls don't keep returning it.
      void clearWizardState(uuid);
      return null;
    }
    return entry.state;
  } catch {
    return null;
  }
}

export async function clearWizardState(uuid: string): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).delete(uuid);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("clear failed"));
    });
  } catch {
    // No-op.
  }
}

/**
 * Returns the wizard's stable session UUID, generating + persisting one on first
 * call. Survives across redirects (localStorage) so a Google SSO round-trip can
 * pick the same key back up after returning to the wizard.
 *
 * Falls back to a per-call random UUID when localStorage is unavailable (private
 * browsing). In that case the snapshot won't survive a navigation, which matches
 * the broader "graceful degrade to today's behaviour" stance.
 */
export function getOrCreateWizardSessionUuid(): string {
  try {
    const existing = window.localStorage.getItem(SESSION_UUID_LS_KEY);
    if (existing && existing.length > 0) return existing;
  } catch {
    // localStorage unavailable — fall through to generate-without-persist.
  }
  const uuid = generateUuid();
  try {
    window.localStorage.setItem(SESSION_UUID_LS_KEY, uuid);
  } catch {
    // No-op; we still return a per-call UUID so the caller has something to key on.
  }
  return uuid;
}

export function clearWizardSessionUuid(): void {
  try {
    window.localStorage.removeItem(SESSION_UUID_LS_KEY);
  } catch {
    // No-op.
  }
}

function generateUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for older browsers / non-secure contexts. Not cryptographically strong
  // but adequate for a session identifier.
  return "wuid-" + Math.random().toString(36).slice(2) + "-" + Date.now().toString(36);
}
